import { FC, useState } from 'react'
import Tree from 'react-d3-dag'
import ArpRegistry from '../../../features/arp-registry/ArpRegistry'
import { getMediaTypeFromIndex } from '../../../__legacy__/api/CoreAPI'
import { useAsyncEffect } from '../platform/explore/common/utils'
import CenteredLoadingAnimation from './CenteredLoadingAnimation'
import { Box } from './Components'

export const getArpMediaGraph = async (arpMediaIndex: number) => {
  const seenNodes = {}

  const traverseSubcomponents = async (index: number) => {
    const indexNode = {}
    const mediaType = await getMediaTypeFromIndex(index)
    if (seenNodes[String(index)]) return seenNodes[String(index)]

    const media = await ArpRegistry.getByIdAsync(index)

    indexNode['name'] = `${media.metadata.title}`
    indexNode['attributes'] = {
      artist: media.user.displayName,
      mediaType,
      id: media.id
    }
    if (media.subComponents.length === 0) return indexNode

    indexNode['children'] = []
    for (let i = 0; i < media.subComponents.length; i++) {
      const subcomponent = media.subComponents[i]
      indexNode['children'].push(await traverseSubcomponents(subcomponent))
    }

    seenNodes[String(index)] = indexNode
    return indexNode
  }

  return await traverseSubcomponents(arpMediaIndex)
}

/**
 * Based on the code found here: https://observablehq.com/@d3/force-directed-tree
 * and here: https://observablehq.com/@d3/tree
 * and here: https://blog.griddynamics.com/using-d3-js-with-react-js-an-8-step-comprehensive-manual/
 */

const useArpMediaGraphVisualizer = (arpMediaIndex: number) => {
  const [hierarchy, setHierarchy] = useState(null)

  // generates the DAG
  useAsyncEffect(async () => {
    setHierarchy(null)
    const graphData = await getArpMediaGraph(arpMediaIndex)
    setHierarchy(graphData)
  }, [arpMediaIndex])

  return hierarchy
}

interface Props {
  arpMediaIndex: number
}

const ArpMediaGraphVisualizer: FC<Props> = ({ arpMediaIndex }) => {
  const hierarchy = useArpMediaGraphVisualizer(arpMediaIndex)
  return (
    <Box css={{ width: '1000%', height: '500%' }}>
      {hierarchy ? (
        <Tree
          data={hierarchy as any}
          collapsible={false}
          onNodeClick={(node) =>
            window.open(
              `/${node.data.attributes.mediaType}?token=${node.data.attributes.id}`,
              '_blank'
            )
          }
        />
      ) : (
        <CenteredLoadingAnimation />
      )}
    </Box>
  )
}

export default ArpMediaGraphVisualizer
